import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Form, Input } from "antd";
import BankTransfer from "./components/BankTransfer";
import UPI from "./components/UPI";
import Paypal from "./components/Paypal";
import Crypto from "./components/Crypto";
import CashWithdraw from "./components/CashWithdraw";
import Easypaisa from "./components/Easypaisa";
import WithdrawRequest from "./WithdrawRequest";
import {
  GetCredentialsApi,
  GetWalletApi,
} from "../../../../api/request/wallet/withdraw";

const WithdrawMobile = ({ setPaymentMethods }) => {
  const [form] = Form.useForm();
  const { userDetails } = useSelector((state) => state?.user);
  const [walletDetails, setWalletDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [amount, setAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [step, setStep] = useState(1);
  const [isRefresh, setIsRefresh] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [validation, setValidation] = useState("");

  const parseAmountLimit = (val) => {
    if (val == null || val === "") return NaN;
    if (typeof val === "number") return val;
    return parseFloat(String(val).replace(/[^0-9.]/g, ""));
  };

  const getAmountError = (usdAmount) => {
    if (usdAmount === undefined || usdAmount === null || usdAmount === "") {
      return "Please enter amount.";
    }
    const num = Number(usdAmount);
    if (isNaN(num) || num <= 0) {
      return "Please enter a valid amount.";
    }
    if (walletDetails?.isRuleActive) {
      const min = parseAmountLimit(walletDetails?.minAmount);
      const max = parseAmountLimit(walletDetails?.maxAmount);
      if (!isNaN(min) && num < min) {
        return `Amount must be at least $${min}.`;
      }
      if (!isNaN(max) && num > max) {
        return `Amount cannot exceed $${max}.`;
      }
    }
    return "";
  };

  const paymentGateways = [
    {
      name: "Bank Transfer",
      value: 1,
      fees: 0,
      processingTime: "1 H",
      paymentLogoUrl: "/assets/images/bank.png",
    },
    {
      name: "PayPal",
      value: 6,
      fees: 0,
      processingTime: "1 H",
      paymentLogoUrl: "/assets/images/paypal.png",
    },
    {
      name: "Crypto",
      value: 6,
      fees: 0,
      processingTime: "1 H",
      paymentLogoUrl: "/assets/images/crypto.png",
    },
    {
      name: "Easypaisa",
      value: 6,
      fees: 0,
      processingTime: "1 H",
      paymentLogoUrl: "/assets/images/easypaisa.png",
    },
    // {  
    //     name: "UPI",
    //     value: 2,
    //     fees: 0,
    //     processingTime: "1 H",
    //     paymentLogoUrl: "/assets/images/upi.png"
    // },
    // {
    //     name: "Paypal",
    //     value: 3,
    //     fees: 0,
    //     processingTime: "1 H",
    //     paymentLogoUrl: "/assets/images/paypal.png"
    // },
    // {
    //     name: "Crypto",
    //     value: 4,
    //     fees: 0.0,
    //     processingTime: "1 H",
    //     paymentLogoUrl: "/assets/images/crypto.png"
    // }
  ];

  const getWalletDetails = async () => {
    setIsLoading(true);
    try {
      const params = { RuleType: "Withdraw" };
      const { data } = await GetWalletApi(
        userDetails?.id,
        new URLSearchParams(params).toString()
      );
      if (data?.success) setWalletDetails(data?.data);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getCredentials = async () => {
    try {
      const { data } = await GetCredentialsApi();
      if (data?.success) {
        setCredentials(data?.data);
      }
    } catch (error) { }
  };

  useEffect(() => {
    getWalletDetails();
    getCredentials();
  }, []);

  useEffect(() => {
    if (paymentGateways.length > 0 && !paymentMethod) {
      const bankTransfer = paymentGateways.find(pg => pg.name === "Bank Transfer") || paymentGateways[0];
      setPaymentMethod(bankTransfer);
      setPaymentMethods(bankTransfer);
    }
  }, [paymentGateways]);

  const validateAmount = (_, value) => {
    const error = getAmountError(value);
    setValidation(error);
    if (error) {
      setStep(1);
      return Promise.reject(new Error(error));
    }
    return Promise.resolve();
  };

  useEffect(() => {
    setValidation(getAmountError(amount));
  }, [amount, walletDetails]);

  const handleSubmit = (values) => {
    if (values?.amount && values?.paymentMethod) setStep(2);
  };

  const resetAll = () => {
    form.resetFields();
    setAmount(0);
    setPaymentMethod(null);
    setStep(1);
    setIsRefresh(true);
  };

  const renderStep = () => {
    switch (paymentMethod?.name) {
      case "BankTransfer":
        return (
          <BankTransfer
            amount={amount}
            resetAll={resetAll}
            details={credentials?.bankDetails}
            validateAmount={validation}
          />
        );
      case "Bank Transfer":
        return <BankTransfer amount={amount} resetAll={resetAll} validateAmount={validation} />;
      case "PayPal":
        return <Paypal amount={amount} resetAll={resetAll} validateAmount={validation} />;
      case "Crypto":
        return <Crypto amount={amount} resetAll={resetAll} validateAmount={validation} />;
      case "Easypaisa":
        return <Easypaisa amount={amount} resetAll={resetAll} validateAmount={validation} />;
      case "Cash":
        return (
          <CashWithdraw
            amount={amount}
            resetAll={resetAll}
            details={credentials?.mobileNumbers}
            validateAmount={validation}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-5">
      <div className="mb-4">
        <div className="relative">
          <Form
            form={form}
            layout="vertical"
            className="space-y-5"
            onFinish={handleSubmit}
          >
            <div>
              <label className="text-lg font-medium text-gray-700 block mb-2">Select Payment Method</label>
              <div className="grid grid-cols-2 gap-2.5">
                {paymentGateways.map((item) => {
                  const isSelected = paymentMethod?.name === item?.name;
                  return (
                    <button
                      key={item?.id}
                      type="button"
                      onClick={() => {
                        const selected = paymentGateways.find(
                          (pg) => pg.name === item.name
                        );
                        setPaymentMethod(selected);
                        setPaymentMethods(selected);
                      }}
                      className={`flex items-center gap-2.5 p-3 rounded-xl border-2 bg-white transition-all duration-200 text-left ${
                        isSelected
                          ? "border-primary shadow-sm"
                          : "border-gray-200"
                      }`}
                    >
                      <img
                        src={item?.paymentLogoUrl}
                        alt={item?.name}
                        className="h-8 w-8 object-contain flex-shrink-0"
                      />
                      <span className={`font-semibold text-xs leading-tight flex-1 ${isSelected ? "text-primary" : "text-gray-800"}`}>
                        {item?.name}
                      </span>
                      <span
                        className={`flex-shrink-0 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                          isSelected
                            ? "border-primary bg-primary"
                            : "border-gray-300 bg-white"
                        }`}
                      >
                        {isSelected && (
                          <span className="h-1.5 w-1.5 rounded-full bg-white" />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <Form.Item
              name="amount"
              label={
                <span className="text-lg text-gray-500 font-medium">
                  Amount In USD
                </span>
              }
              rules={[{ validator: validateAmount }]}
            >
              <Input
                type="number"
                placeholder="Amount in USD"
                size="large"
                onChange={(e) => setAmount(e?.target?.value)}
                className="py-3"
              />
            </Form.Item>
          </Form>
        </div>
      </div>

      {renderStep()}

      <WithdrawRequest isRefresh={isRefresh} setIsRefresh={setIsRefresh} />
    </div>
  );
};

export default WithdrawMobile;
